import type { Schedule, Member, ChekiPricing, ChekiType } from '@tsuyosa/types';

const API_URL = import.meta.env.VITE_API_URL ?? '';

async function publicFetch<T>(path: string, params?: Record<string, string>): Promise<T> {
  const q = params ? `?${new URLSearchParams(params).toString()}` : '';
  const res = await fetch(`${API_URL}${path}${q}`);
  const json = await res.json() as { success: boolean; data: T; error?: { message: string } };
  if (!res.ok || !json.success) throw new Error(json.error?.message ?? `API error at ${path}`);
  return json.data;
}

export function getChekiSchedules(): Promise<Schedule[]> {
  return publicFetch<Schedule[]>('/api/v1/schedules', { cheki_enabled: 'true', status: 'upcoming' });
}

export interface ScheduleChekiData {
  cheki_enabled: boolean;
  pricing: ChekiPricing[];
}

export function getScheduleCheki(scheduleId: string): Promise<ScheduleChekiData> {
  return publicFetch<ScheduleChekiData>(`/api/v1/schedules/${scheduleId}/cheki`);
}

export function getPublicMembers(): Promise<Member[]> {
  return publicFetch<Member[]>('/api/v1/members', { activity_status: 'active' });
}

export interface ChekiOrderPayload {
  schedule_id: string;
  buyer_name:  string;
  email:       string;
  contact?:    string;
  type:        ChekiType;
  items?:      { member_id: string; quantity: number }[];
  quantity?:   number;
}

export interface CreateOrderResult {
  id:                  string;
  order_token:         string;
  payment_deadline_at: string;
}

export async function createChekiOrder(
  payload: ChekiOrderPayload,
  token?: string | null,
): Promise<CreateOrderResult> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_URL}/api/v1/cheki/orders`, {
    method: 'POST', headers, body: JSON.stringify(payload),
  });
  const json = await res.json() as { success: boolean; data: CreateOrderResult; error?: { message: string } };
  if (!res.ok || !json.success) throw new Error(json.error?.message ?? 'Gagal membuat order');
  return json.data;
}

export async function uploadChekiProof(file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const res  = await fetch(`${API_URL}/api/v1/cheki/upload/proof`, { method: 'POST', body: form });
  const json = await res.json() as { success: boolean; data: { url: string }; error?: { message: string } };
  if (!res.ok || !json.success) throw new Error(json.error?.message ?? 'Gagal upload bukti bayar');
  return json.data.url;
}

export async function submitChekiProof(
  orderId: string,
  proofUrl: string,
  orderToken: string,
): Promise<void> {
  const res = await fetch(
    `${API_URL}/api/v1/cheki/orders/${orderId}/proof?token=${encodeURIComponent(orderToken)}`,
    {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ payment_proof_url: proofUrl }),
    },
  );
  if (res.status === 410) throw new Error('DEADLINE_EXPIRED');
  const json = await res.json() as { success: boolean; error?: { message: string } };
  if (!res.ok || !json.success) throw new Error(json.error?.message ?? 'Gagal konfirmasi pembayaran');
}

export interface OrderStatusData {
  id:                  string;
  type:                string;
  status:              string;
  buyer_name:          string;
  email:               string;
  contact:             string | null;
  total_price:         number;
  payment_proof_url:   string | null;
  payment_deadline_at: string | null;
  created_at:          string;
  schedule:            { title: string; date: string } | null;
  items:               Array<{ member_id: string | null; nickname: string | null; quantity: number; price: number }> | null;
}

export async function getChekiOrderStatus(
  orderId: string,
  token?: string,
  accessToken?: string | null,
): Promise<OrderStatusData> {
  const url = `${API_URL}/api/v1/cheki/orders/${orderId}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
  const headers: Record<string, string> = {};
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  const res  = await fetch(url, { headers });
  const json = await res.json() as { success: boolean; data: OrderStatusData; error?: { message: string } };
  if (!res.ok || !json.success) throw new Error(json.error?.message ?? 'Order tidak ditemukan');
  return json.data;
}

export async function getPaymentInfo(): Promise<{ image_url: string | null }> {
  return publicFetch<{ image_url: string | null }>('/api/v1/settings/payment-info');
}
